import cn from 'classnames';
import { FreeMode, Navigation, Autoplay } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Service } from '@/api/cms/services/type';
import type { DefaultCmsDataResponse } from '@/api/cms/types';
import Button from '@/components/Button';
import ChevronLeftIcon from '@/components/icons/ChevronLeftIcon';
import ChevronRightIcon from '@/components/icons/ChevronRightIcon';
import type { CityId } from '@/lib/constants';
import useIsMobile from '@/lib/useIsMobile';
import DivideSliderItem from '@/modules/service/DivideSliderItem';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';
import ServiceSliderItem from '../../ServiceSliderItem';
import SliderDescriptionItem from '../../SliderDescriptionItem';
import SliderTextItem from '../../SliderTextItem';

type Props = {
  services: DefaultCmsDataResponse<Service>[];
  cityId?: CityId;
};

function AhaTruckPriceSlider({ services, cityId }: Props) {
  const isMobile = useIsMobile();
  const classButtonNavSwiper =
    'group fixed top-1/2 z-30 col-span-1 flex h-[72px] w-9 items-center bg-neutral-15 shadow-shadow8 enabled:hover:bg-neutral-10 enabled:hover:w-16 disabled:opacity-50 transition ease-out duration-200';
  return (
    <section className="bg-white">
      <div className="max-w-8xl desktop:px-8 mx-auto px-4">
        <button
          aria-label="button"
          className={cn(
            classButtonNavSwiper,
            'prevEl left-0 flex justify-end rounded-r-full pr-2'
          )}
        >
          <ChevronLeftIcon className="fill-neutral-50 group-disabled:opacity-50" />
        </button>
        <button
          aria-label="button"
          className={cn(
            classButtonNavSwiper,
            'nextEl right-0 rounded-l-full pl-2'
          )}
        >
          <ChevronRightIcon className="fill-neutral-50  group-disabled:opacity-50" />
        </button>
        <Swiper
          slidesPerView={isMobile ? 2 : 4}
          spaceBetween={16}
          loop={false}
          autoplay={{ delay: 3000 }}
          loopFillGroupWithBlank={true}
          navigation={{ prevEl: '.prevEl', nextEl: '.nextEl' }}
          modules={[FreeMode, Navigation, Autoplay]}
          className="relative"
        >
          {services.map((item, idx) => (
            <SwiperSlide
              key={idx}
              className="flex flex-col justify-start bg-white pt-10 pb-20 text-center"
            >
              <ServiceSliderItem
                name={item.attributes.name}
                desc={item.attributes.description}
                icon={item.attributes?.icon?.data.attributes?.url}
                className="h-64"
              />

              <div className="mb-6 h-12"></div>

              <article
                className={cn(
                  'flex flex-col items-center justify-start space-y-2',
                  {
                    'min-h-[152px]': !cityId,
                    'min-h-[24px]': cityId,
                  }
                )}
              >
                {item.attributes?.locations?.data?.map((item1) => (
                  <p
                    key={item1.id}
                    className="text-body16 font-normal text-black"
                  >
                    {item1.attributes?.name}
                  </p>
                ))}
              </article>

              <div className="my-6 h-12"></div>

              <div className="h-6"></div>

              <div className="my-6 h-12"></div>

              {item.attributes.stoppoint_amount ? (
                <SliderTextItem
                  items={[item.attributes.stoppoint_amount]}
                  className="min-h-[24px]"
                />
              ) : (
                <div className="h-6"></div>
              )}

              <div className="my-6 h-12"></div>

              {item.attributes.stoppoint_fee ? (
                <SliderTextItem
                  items={[item.attributes.stoppoint_fee]}
                  className="min-h-[24px]"
                />
              ) : (
                <div className="h-6"></div>
              )}

              <div className="my-6 h-12"></div>

              <div className="h-6"></div>

              <div className="my-6 h-12"></div>

              {item.attributes.vehicle_size ? (
                <SliderTextItem
                  items={[item.attributes.vehicle_size]}
                  className="min-h-[24px]"
                />
              ) : (
                <div className="h-6"></div>
              )}

              <div className="my-6 h-12"></div>

              {item.attributes?.delivery_fee && (
                <SliderDescriptionItem
                  items={item.attributes?.delivery_fee}
                  className="min-h-[calc(60px*5+24px*3)]"
                />
              )}

              <div className="my-6 h-12"></div>

              <div className="h-[60px]"></div>

              <div className="my-6 h-12"></div>

              <div className="h-6"></div>

              <div className="my-6 h-12"></div>

              <div className="h-[60px]"></div>

              {item.attributes?.extra ? (
                <SliderDescriptionItem
                  items={item.attributes?.extra}
                  className="min-h-[calc(60px*4+24px*3)]"
                />
              ) : (
                <div className="h-[calc(60px*4+24px*3)]"></div>
              )}
            </SwiperSlide>
          ))}
          <DivideSliderItem
            title="Khu vực áp dụng"
            className="top-[calc(264px+32px)]"
          />

          <DivideSliderItem
            title="Thời gian giao hàng"
            className={cn({
              'top-[calc(264px+32px+152px+24px*2+48px)]': !cityId,
              'top-[calc(264px+32px+24px+24px*2+48px)]': cityId,
            })}
          />

          <p
            className={cn('text-body16 absolute z-20 h-6 w-full text-center', {
              'top-[calc(264px+32px+152px+24px*2+48px+24px+48px)]': !cityId,
              'top-[calc(264px+32px+24px+24px*2+48px+24px+48px)]': cityId,
            })}
          >
            <strong>Trong ngày</strong> (áp dụng tất cả)
          </p>

          <DivideSliderItem
            title="Số điểm dừng / điểm giao"
            className={cn({
              'top-[calc(264px+32px+152px+24px*2+48px+24px+48px+24px*2)]':
                !cityId,
              'top-[calc(264px+32px+24px+24px*2+48px+24px+48px+24px*2)]':
                cityId,
            })}
          />

          <DivideSliderItem
            title="Phí điểm dừng"
            className={cn({
              'top-[calc(264px+32px+152px+24px*2+48px+24px+48px+24px*2+24px+48px*2)]':
                !cityId,
              'top-[calc(264px+32px+24px+24px*2+48px+24px+48px+24px*2+24px+48px*2)]':
                cityId,
            })}
          />

          <DivideSliderItem
            title="Phí quay đầu"
            className={cn({
              'top-[calc(264px+32px+152px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px)]':
                !cityId,
              'top-[calc(264px+32px+24px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px)]':
                cityId,
            })}
          />

          <p
            className={cn(
              'text-body16 absolute z-20 h-6 w-full items-center justify-center text-center font-bold',
              {
                'top-[calc(264px+32px+152px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px)]':
                  !cityId,
                'top-[calc(264px+32px+24px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px)]':
                  cityId,
              }
            )}
          >
            50% tổng km chiều đi
          </p>

          <DivideSliderItem
            title="Kích thước lòng thùng xe"
            className={cn({
              'top-[calc(264px+32px+152px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px)]':
                !cityId,
              'top-[calc(264px+32px+24px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px)]':
                cityId,
            })}
          />

          <DivideSliderItem
            title="Phí quãng đường"
            className={cn({
              'top-[calc(264px+32px+120px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px*2)]':
                !cityId,
              'top-[calc(264px+32px+24px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px)]':
                cityId,
            })}
          />

          <DivideSliderItem
            title="Phụ phí dịch vụ giao hàng tạm ứng"
            className={cn({
              'top-[calc(264px+32px+152px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px+60px*6+24px*5+24px*4)]':
                !cityId,
              'top-[calc(264px+32px+24px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px+60px*5+24px*4+24px*4)]':
                cityId,
            })}
          />

          <article
            className={cn(
              'absolute z-20 flex min-h-[84px] w-full flex-row items-center justify-center space-x-16 text-center',
              {
                'top-[calc(264px+32px+120px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px+60px*5+24px*3+24px*4+48px*2+24px*3)]':
                  !cityId,
                'top-[calc(264px+32px+24px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px+60px*4+24px*3+24px*4+48px+24px*2)]':
                  cityId,
              }
            )}
          >
            {services[0].attributes.cod?.map((item, idx) => (
              <SliderDescriptionItem items={[item]} key={idx} />
            ))}
          </article>

          <DivideSliderItem
            title="Phí chờ"
            className={cn({
              'top-[calc(264px+32px+152px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px+60px*5+24px*3+24px*4+48px+24px*2+60px+24px*2)]':
                !cityId,
              'top-[calc(264px+32px+24px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px+60px*5+24px*3+24px*4+48px+24px*2+60px+24px*2)]':
                cityId,
            })}
          />

          <p
            className={cn(
              'text-body16 absolute z-20 h-6 w-full items-center justify-center text-center font-bold',
              {
                'top-[calc(264px+32px+152px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px+60px*5+24px*3+24px*4+48px+24px*2+60px+24px*3+48px)]':
                  !cityId,
                'top-[calc(264px+32px+24px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px+60px*5+24px*3+24px*4+48px+24px*2+60px+24px*3+48px)]':
                  cityId,
              }
            )}
          >
            Phí chờ (60,000VNĐ/giờ)
          </p>

          <DivideSliderItem
            title="Yêu cầu thêm"
            className={cn({
              'top-[calc(264px+32px+152px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px+60px*5+24px*3+24px*4+48px+24px*2+60px+24px*3+48px+24px*2)]':
                !cityId,
              'top-[calc(264px+32px+24px+24px*2+48px+24px+48px+24px*2+24px+48px*2+24px*3+48px*2+24px+48px+48px*2+24px+60px*5+24px*3+24px*4+48px+24px*2+60px+24px*3+48px+24px*2)]':
                cityId,
            })}
          />
        </Swiper>
        <div className="desktop:mb-[100px] mb-8 flex w-full items-center justify-center">
          <Button
            className="w-1/4 max-w-xs px-12 py-8"
            type="link"
            href="https://ahamove.onelink.me/fmLb/dq04k5xx "
          >
            Lên đơn ngay
          </Button>
        </div>
      </div>
    </section>
  );
}

export default AhaTruckPriceSlider;
